import { useTranslation } from 'react-i18next'
import { useHotkeys } from 'react-hotkeys-hook'
import { VIEW_TYPE_OPTIONS } from '../constants/viewTypeOptions'
import { useChangeViewType, useCurrentViewType } from '../hooks/useCurrentViewType'
import { cn } from '@/lib/utils'

interface ViewTypeSwitcherProps {
  projectId: string
  isSpace?: boolean
}

/**
 * The only place to switch layout (design 26b): List / Board / Calendar /
 * Timeline. The active one is a white pill with a ring and an accent icon,
 * so "which layout am I in?" is never a question. ⇧1–⇧4 switch.
 */
export function ViewTypeSwitcher({ projectId, isSpace = false }: ViewTypeSwitcherProps) {
  const { t } = useTranslation('projects')
  const currentView = useCurrentViewType(projectId)
  const changeViewType = useChangeViewType(projectId, isSpace)

  useHotkeys(
    'shift+1, shift+2, shift+3, shift+4',
    (_event, handler) => {
      const index = Number(handler.keys?.[0]) - 1
      const option = VIEW_TYPE_OPTIONS[index]
      if (option) changeViewType(option.value)
    },
    { preventDefault: true },
    [changeViewType],
  )

  return (
    <div
      role="radiogroup"
      aria-label={t('layout.title')}
      className="flex h-[30px] items-center gap-0.5 rounded-lg bg-secondary p-0.5 phone:h-10 phone:w-full"
    >
      {VIEW_TYPE_OPTIONS.map(({ value, labelKey, icon: Icon }, index) => {
        const selected = value === currentView
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-keyshortcuts={`Shift+${index + 1}`}
            title={`${t(labelKey)} (⇧${index + 1})`}
            onClick={() => changeViewType(value)}
            className={cn(
              'flex h-[26px] items-center gap-1.5 rounded-md px-2.5 text-[12.5px] transition-colors phone:h-9 phone:flex-1 phone:justify-center phone:text-[13px]',
              selected
                ? 'bg-card font-semibold text-foreground shadow-xs ring-1 ring-border'
                : 'font-medium text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className={cn('size-3.5 shrink-0', selected && 'text-brand')} />
            <span className="hidden lg:inline phone:inline">{t(labelKey)}</span>
          </button>
        )
      })}
    </div>
  )
}
